/* eslint-disable no-console */
import { delay } from 'redux-saga';
import { put, takeLatest, select, call } from 'redux-saga/effects';
import { searchElementMatch, setNotification, operatorTyping } from 'utils';
import moment from 'moment';
import * as FormActions from '../actions/formActions';
import * as FormTypes from '../types/formTypes';
import * as FormApi from '../api/formApi';
import * as FormVersionApi from '../api/formVersionApi';

const getForms = state => state.forms;
const getLayout = state => state.layout;

// L O A D   F O R M S
function* loadFormsSaga() {
    try {
        const { offset, limit, filters, sort } = yield select(getForms);

        const params = {
            offset,
            limit,
            joins: JSON.stringify(['order_item']),
        };

        if (sort !== null) {
            const sortInt = sort.type === 'asc' ? 1 : -1;

            if (sort.key === 'quality') {
                params.sort_by = JSON.stringify({ quality: sortInt });
            } else if (sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sortInt });
            } else if (sort.key === 'form_number') {
                params.sort_by = JSON.stringify({ form_number: sortInt });
            } else if (sort.key === 'status') {
                params.sort_by = JSON.stringify({ status: sortInt });
            } else if (sort.key === 'shipping_date') {
                params.sort_by = JSON.stringify({ shipping_date: sortInt });
            } else if (sort.key === 'print_date') {
                params.sort_by = JSON.stringify({ print_date: sortInt });
            } else if (sort.key === 'delivery_date') {
                params.sort_by = JSON.stringify({ delivery_date: sortInt });
            }
        }

        const filterParams = {};
        const filterFields = ['sizes', 'form_number', 'form_version_id', 'design_id', 'customer'];
        filterFields.forEach((field) => {
            if (filters[field] !== '') {
                filterParams[field] = ['sizes'].includes(field) ? filters[field].trim() : operatorTyping(filters[field].trim());
            }
        });

        if (filters.order_id !== '') {
            filterParams.order_number = operatorTyping(filters.order_id);
        }

        if (filters.order_item_id !== '') {
            filterParams.join = {
                'order_items.order_item_number': operatorTyping(filters.order_item_id),
            };
        }

        if (filters.quality !== '') {
            filterParams['configurations_property.name'] = searchElementMatch(filters.quality, 'fr');
        }

        if (filters.segment_repetition) {
            filterParams.segment_repetition = parseInt(filters.segment_repetition, 10);
        }

        if (filters.reprint !== '') {
            filterParams.reprint = filters.reprint;
        }

        if (filters.rollType !== undefined) {
            filterParams.rolls_size = filters.rollType;
        }

        if (filters.shippingDate.length > 0) {
            const customDate = [
                moment.utc(filters.shippingDate[0]).format('YYYY-MM-DD 00:00:00'),
                moment.utc(filters.shippingDate[1]).format('YYYY-MM-DD 23:59:59'),
            ];
            filterParams.shipping_date = { between: customDate };
        }

        if (filters.state !== undefined) {
            if (filters.state === 'NOT_DONE') {
                filterParams.state = ['WAITING_FOR_MANUFACTURING', 'PLANNED', 'IN_PROGRESS'];
            } else {
                filterParams.state = filters.state;
            }
        }

        if (filters.withBorder !== undefined) {
            filterParams['configurations_property.code'] = filters.withBorder;
        }

        params.filter = JSON.stringify(filterParams);

        const result = yield FormApi.getFormsApi(params);
        yield put(FormActions.loadFormsRes(result.data));
    } catch (e) {
        console.log(e);
        yield put(FormActions.loadFormsErr());
    }
}

export function* loadFormsWatcher() {
    yield takeLatest(FormTypes.LOAD_FORMS, loadFormsSaga);
}
// L O A D   F O R M S

// O N L Y   D E L E T E   F O R M
function* onlyDeleteForm(action) {
    const { locale } = yield select(getLayout);
    const formResult = yield FormApi.deleteFormApi({ form_id: action.data.formId });

    if (formResult.status === 202) {
        yield put(FormActions.deleteFormRes());
        setNotification('success', locale, 'global.success', 'forms.notification.delete.success');
        yield call(loadFormsSaga, {});
    } else {
        const error = yield formResult.json();
        setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
        yield put(FormActions.deleteFormErr());
    }
}
// O N L Y   D E L E T E   F O R M


// D E L E T E   F O R M
function* deleteFormSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const processingFV = yield FormVersionApi.getFormVersionsApi({
            filter: JSON.stringify({ temporary: true }),
        });

        if (processingFV.data.hits.length === 0) {
            if (action.data.formVersionIds.bulk.length > 0) {
                const formVersionResult = yield FormVersionApi.deleteFormVersionsApi(action.data.formVersionIds);

                if (formVersionResult.status === 202) {
                    yield call(onlyDeleteForm, action);
                } else {
                    setNotification('error', locale, 'global.error', 'formVersion.notification.deleteForm.error');
                    yield put(FormActions.deleteFormErr());
                }
            } else {
                yield call(onlyDeleteForm, action);
            }
        } else {
            yield delay(2000);
            yield call(deleteFormSaga, action);
        }
    } catch (e) {
        yield put(FormActions.deleteFormErr());
        setNotification('error', locale, 'global.error', 'forms.notification.delete.error');
        console.log(e);
    }
}

export function* deleteFormWatcher() {
    yield takeLatest(FormTypes.DELETE_FORM, deleteFormSaga);
}
// D E L E T E   F O R M


// U P D A T E   F O R M
function* updateFormSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const result = yield FormApi.updateFormApi(action.data.form);

        if (result.status === 202) {
            const resultOrderItem = yield FormApi.editOrderItemApi(action.data.orderItem);

            if (resultOrderItem.status === 202) {
                yield put(FormActions.updateFormRes(action.data.form_id, true));
                setNotification('success', locale, 'global.success', 'forms.notification.edit.success');
                yield call(loadFormsSaga, {});

                yield delay(1500);
                yield put(FormActions.updateFormRes(false, false));
            }
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(FormActions.updateFormErr());
        }
    } catch (e) {
        yield put(FormActions.updateFormErr());
        setNotification('error', locale, 'global.error', 'forms.notification.edit.error');
        console.log(e);
    }
}

export function* updateFormWatcher() {
    yield takeLatest(FormTypes.UPDATE_FORM, updateFormSaga);
}
// U P D A T E   F O R M
